import React, { useEffect, useState, useCallback } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, AlertTriangle, X, Navigation, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { fmtMoney, fmtDate } from "@/lib/format";
import { statusMeta } from "@/lib/statusColors";
import { PageHeader, Loading, SectionCard } from "@/components/common";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";
import { JobCompletion } from "@/components/JobCompletion";

const hexA = (hex, a) => (hex && hex.startsWith("#") ? hex + a : "#94A3B8" + a);

function VisitChip({ v, draggable, onDragStart, onClick }) {
  const m = statusMeta(v.status);
  return (
    <button
      data-testid={`calendar-visit-${v.id}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, v)}
      onClick={() => onClick(v)}
      className="w-full text-left rounded-md px-2 py-1 mb-1 text-xs border-l-[3px] hover:shadow-sm transition-shadow cursor-pointer"
      style={{ backgroundColor: hexA(v.consultant_colour, "1A"), borderLeftColor: m.dot }}
    >
      <div className="flex items-center gap-1 font-medium text-slate-700 truncate">
        <span>{v.start_time}</span>
        <span className="truncate">{v.client_name}</span>
      </div>
      <div className="text-slate-500 truncate">{v.service_name || v.job_title}</div>
      {v.consultant_name && <div className="text-[10px] text-slate-400 truncate">{v.consultant_name}</div>}
    </button>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const canSchedule = can(user, "schedule_jobs");
  const canReassign = can(user, "reassign_jobs") || canSchedule;
  const canOverride = can(user, "override_scheduling_limits");
  const canComplete = can(user, "complete_jobs");
  const canComms = can(user, "view_clients");

  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buFilter, setBuFilter] = useState("");
  const [consFilter, setConsFilter] = useState("");
  const [bus, setBus] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [selected, setSelected] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [conflictState, setConflictState] = useState(null); // {visit, updates, conflicts}

  const range = useCallback(() => {
    if (view === "month") {
      const s = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
      const e = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
      return [s, e];
    }
    if (view === "week") {
      return [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
    }
    return [cursor, cursor];
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, e] = range();
    try {
      const params = new URLSearchParams({ start: format(s, "yyyy-MM-dd"), end: format(e, "yyyy-MM-dd") });
      if (buFilter) params.set("business_unit_id", buFilter);
      if (consFilter) params.set("consultant_id", consFilter);
      const { data } = await api.get(`/calendar?${params}`);
      setData(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, [range, buFilter, consFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/business-units").then((r) => setBus(r.data)).catch(() => {}); }, []);

  const visitsByDay = (day) => (data?.visits || []).filter((v) => v.scheduled_date && isSameDay(parseISO(v.scheduled_date), day));
  const leaveByDay = (day) => (data?.leave || []).filter((l) => {
    const s = parseISO(l.start_date), e = parseISO(l.end_date);
    return day >= s && day <= addDays(e, 0);
  });
  const holidayByDay = (day) => (data?.holidays || []).find((h) => isSameDay(parseISO(h.date), day));

  const onDragStart = (e, v) => { e.dataTransfer.setData("visitId", v.id); };

  const applyUpdate = async (visit, updates, override = false, reason = "") => {
    try {
      await api.put(`/visits/${visit.id}`, { ...updates, override, reason });
      toast.success("Schedule updated");
      setConflictState(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.conflicts) {
        setConflictState({ visit, updates, conflicts: detail.conflicts });
      } else {
        toast.error(apiError(err));
        load();
      }
    }
  };

  const onDropDate = (e, day) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("visitId");
    const v = data.visits.find((x) => x.id === id);
    if (!v || !canSchedule) return;
    const newDate = format(day, "yyyy-MM-dd");
    if (v.scheduled_date?.slice(0, 10) === newDate) return;
    applyUpdate(v, { scheduled_date: newDate });
  };

  const onDropConsultant = (e, consId) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("visitId");
    const v = data.visits.find((x) => x.id === id);
    if (!v || !canReassign) return;
    if (v.consultant_id === consId) return;
    applyUpdate(v, { consultant_id: consId, scheduled_date: v.scheduled_date });
  };

  const title = view === "month" ? format(cursor, "MMMM yyyy") : view === "week"
    ? `Week of ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM")}` : format(cursor, "EEEE d MMMM yyyy");

  const step = (dir) => {
    if (view === "month") setCursor(addMonths(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addDays(cursor, dir));
  };

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Drag jobs to reschedule · colour-coded by consultant & status" icon={CalendarDays}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="calendar-bu-filter" value={buFilter} onChange={(e) => setBuFilter(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5">
              <option value="">All business units</option>
              {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select data-testid="calendar-consultant-filter" value={consFilter} onChange={(e) => setConsFilter(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5">
              <option value="">All consultants</option>
              {(data?.consultants || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        } />

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => step(-1)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft size={18} /></button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Today</button>
          <button data-testid="cal-next" onClick={() => step(1)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight size={18} /></button>
          <span className="font-display font-semibold text-slate-800 ml-1">{title}</span>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {["month", "week", "day"].map((vw) => (
            <button key={vw} data-testid={`cal-view-${vw}`} onClick={() => setView(vw)}
              className={`px-3 py-1.5 text-sm capitalize ${view === vw ? "bg-seq-forest text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{vw}</button>
          ))}
        </div>
      </div>

      {loading && !data ? <Loading /> : (
        <>
          {view === "month" && <MonthView cursor={cursor} visitsByDay={visitsByDay} leaveByDay={leaveByDay} holidayByDay={holidayByDay}
            draggable={canSchedule} onDragStart={onDragStart} onDropDate={onDropDate} dragOver={dragOver} setDragOver={setDragOver} onClick={setSelected} />}
          {view === "week" && <WeekView cursor={cursor} visitsByDay={visitsByDay} leaveByDay={leaveByDay} holidayByDay={holidayByDay}
            draggable={canSchedule} onDragStart={onDragStart} onDropDate={onDropDate} dragOver={dragOver} setDragOver={setDragOver} onClick={setSelected} />}
          {view === "day" && <DayView cursor={cursor} data={data} visitsByDay={visitsByDay} leaveByDay={leaveByDay} holidayByDay={holidayByDay}
            draggable={canReassign} onDragStart={onDragStart} onDropConsultant={onDropConsultant} dragOver={dragOver} setDragOver={setDragOver} onClick={setSelected} />}
        </>
      )}

      <Legend consultants={data?.consultants || []} />

      {selected && <VisitDetail visit={selected} onClose={() => setSelected(null)} consultants={data?.consultants || []}
        canSchedule={canSchedule} canReassign={canReassign} canComplete={canComplete} canComms={canComms}
        onComplete={(id) => { setSelected(null); setCompleting(id); }}
        onUpdated={() => { setSelected(null); load(); }} />}

      {completing && <JobCompletion visitId={completing} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); }} />}

      {conflictState && (
        <ConflictDialog state={conflictState} canOverride={canOverride}
          onCancel={() => { setConflictState(null); load(); }}
          onOverride={(reason) => applyUpdate(conflictState.visit, conflictState.updates, true, reason)} />
      )}
    </div>
  );
}

function DayCell({ day, cursor, children, leave, holiday, monthMode, onDropDate, dragOver, setDragOver }) {
  const isOtherMonth = monthMode && !isSameMonth(day, cursor);
  const isToday = isSameDay(day, new Date());
  const key = format(day, "yyyy-MM-dd");
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
      onDragLeave={() => setDragOver((d) => (d === key ? null : d))}
      onDrop={(e) => onDropDate(e, day)}
      data-testid={`calendar-day-${key}`}
      className={`min-h-[112px] border-r border-b border-slate-100 p-1.5 ${isOtherMonth ? "bg-slate-50/50" : "bg-white"} ${dragOver === key ? "drag-over" : ""}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-medium h-6 w-6 flex items-center justify-center rounded-full ${isToday ? "bg-seq-green text-white" : isOtherMonth ? "text-slate-300" : "text-slate-500"}`}>{format(day, "d")}</span>
        {holiday && <span className="text-[9px] bg-sky-100 text-sky-700 px-1 rounded truncate max-w-[70px]" title={holiday.name}>{holiday.name}</span>}
      </div>
      {leave.length > 0 && (
        <div className="text-[10px] text-red-600 bg-red-50 rounded px-1 mb-1 truncate" title={leave.map((l) => `${l.consultant_name} (${l.leave_type})`).join(", ")}>
          {leave.length} on leave
        </div>
      )}
      {children}
    </div>
  );
}

function MonthView(p) {
  const { cursor } = p;
  const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const vs = p.visitsByDay(day);
          return (
            <DayCell key={day} day={day} cursor={cursor} leave={p.leaveByDay(day)} holiday={p.holidayByDay(day)} monthMode
              onDropDate={p.onDropDate} dragOver={p.dragOver} setDragOver={p.setDragOver}>
              <div className="space-y-0.5">
                {vs.slice(0, 3).map((v) => <VisitChip key={v.id} v={v} draggable={p.draggable} onDragStart={p.onDragStart} onClick={p.onClick} />)}
                {vs.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{vs.length - 3} more</div>}
              </div>
            </DayCell>
          );
        })}
      </div>
    </div>
  );
}

function WeekView(p) {
  const start = startOfWeek(p.cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden grid grid-cols-1 sm:grid-cols-7">
      {days.map((day) => {
        const vs = p.visitsByDay(day);
        const key = format(day, "yyyy-MM-dd");
        const isToday = isSameDay(day, new Date());
        return (
          <div key={key} className="border-r border-slate-100 min-h-[400px]"
            onDragOver={(e) => { e.preventDefault(); p.setDragOver(key); }}
            onDragLeave={() => p.setDragOver((d) => (d === key ? null : d))}
            onDrop={(e) => p.onDropDate(e, day)}>
            <div className={`px-2 py-2 border-b border-slate-100 text-center ${isToday ? "bg-seq-green/10" : "bg-slate-50"}`}>
              <div className="text-xs text-slate-400">{format(day, "EEE")}</div>
              <div className={`text-sm font-semibold ${isToday ? "text-seq-green" : "text-slate-700"}`}>{format(day, "d")}</div>
            </div>
            <div className={`p-1.5 ${p.dragOver === key ? "drag-over" : ""} min-h-[350px]`}>
              {p.holidayByDay(day) && <div className="text-[10px] bg-sky-100 text-sky-700 px-1 rounded mb-1">{p.holidayByDay(day).name}</div>}
              {p.leaveByDay(day).length > 0 && <div className="text-[10px] text-red-600 bg-red-50 rounded px-1 mb-1">{p.leaveByDay(day).length} on leave</div>}
              {vs.map((v) => <VisitChip key={v.id} v={v} draggable={p.draggable} onDragStart={p.onDragStart} onClick={p.onClick} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView(p) {
  const consultants = p.data?.consultants || [];
  const dayVisits = p.visitsByDay(p.cursor);
  const holiday = p.holidayByDay(p.cursor);
  const leaveIds = new Set(p.leaveByDay(p.cursor).map((l) => l.consultant_id));
  const cols = [...consultants, { id: "__un", name: "Unassigned", colour: "#94A3B8", max_jobs: 0 }];
  return (
    <div>
      {holiday && <div className="mb-3 text-sm bg-sky-50 text-sky-700 border border-sky-200 rounded-lg px-3 py-2">Public holiday: {holiday.name}</div>}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(180px, 1fr))` }}>
        {cols.map((c) => {
          const vs = dayVisits.filter((v) => (c.id === "__un" ? !v.consultant_id : v.consultant_id === c.id));
          const onLeave = leaveIds.has(c.id);
          const over = c.max_jobs > 0 && vs.length > c.max_jobs;
          return (
            <div key={c.id} className={`bg-white border rounded-xl overflow-hidden ${p.dragOver === c.id ? "drag-over" : "border-slate-200"}`}
              onDragOver={(e) => { e.preventDefault(); p.setDragOver(c.id); }}
              onDragLeave={() => p.setDragOver((d) => (d === c.id ? null : d))}
              onDrop={(e) => c.id !== "__un" && p.onDropConsultant(e, c.id)}>
              <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between" style={{ backgroundColor: hexA(c.colour, "12") }}>
                <span className="flex items-center gap-2 text-sm font-medium text-slate-700 truncate">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.colour }} />{c.name}
                </span>
                {c.id !== "__un" && (
                  <span className={`text-xs font-semibold ${over ? "text-red-600" : onLeave ? "text-red-500" : "text-slate-400"}`}>
                    {onLeave ? "Leave" : `${vs.length}/${c.max_jobs}`}
                  </span>
                )}
              </div>
              <div className="p-2 min-h-[220px] space-y-1">
                {over && <div className="text-[11px] text-red-600 flex items-center gap-1 mb-1"><AlertTriangle size={12} /> Over capacity</div>}
                {onLeave && <div className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1 mb-1">On leave today</div>}
                {vs.map((v) => <VisitChip key={v.id} v={v} draggable={p.draggable} onDragStart={p.onDragStart} onClick={p.onClick} />)}
                {vs.length === 0 && !onLeave && <div className="text-xs text-slate-300 text-center py-6">No jobs</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend({ consultants }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
      <span className="font-medium text-slate-600">Consultants:</span>
      {consultants.map((c) => (
        <span key={c.id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.colour }} />{c.name}</span>
      ))}
    </div>
  );
}

function ConflictDialog({ state, canOverride, onCancel, onOverride }) {
  const [reason, setReason] = useState("");
  const hard = state.conflicts.filter((c) => c.severity === "error");
  return (
    <Modal open onOpenChange={onCancel} title="Scheduling conflict" testId="conflict-dialog"
      description="This move breaks one or more hard scheduling rules.">
      <div className="space-y-2">
        {state.conflicts.map((c, i) => (
          <div key={i} className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${c.severity === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {c.message}
          </div>
        ))}
      </div>
      {canOverride ? (
        <div className="space-y-2 pt-2">
          <label className="text-sm font-medium text-slate-700">Override reason (required)</label>
          <input data-testid="override-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Client requested this specific date"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-seq-green focus:ring-2 focus:ring-seq-green/20 outline-none" />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
            <button data-testid="confirm-override-btn" disabled={!reason} onClick={() => onOverride(reason)}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg disabled:opacity-50">Override & move</button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end pt-2">
          <button onClick={onCancel} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Close</button>
        </div>
      )}
    </Modal>
  );
}

function VisitDetail({ visit, onClose, consultants, canSchedule, canReassign, canComplete, canComms, onComplete, onUpdated }) {
  const [conflicts, setConflicts] = useState([]);
  const [form, setForm] = useState({ scheduled_date: visit.scheduled_date?.slice(0, 10), start_time: visit.start_time, consultant_id: visit.consultant_id || "", status: visit.status });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/visits/${visit.id}/conflicts`).then((r) => setConflicts(r.data)).catch(() => {}); }, [visit.id]);

  const sendConfirmation = async () => {
    try { await api.post("/communications/send", { visit_id: visit.id, message_type: "Appointment confirmation" }); toast.success("Confirmation email queued (logged if no provider key)"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openReport = async () => {
    try {
      const res = await api.get(`/visits/${visit.id}/report.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  const save = async (override = false, reason = "") => {
    setSaving(true);
    try {
      await api.put(`/visits/${visit.id}`, { ...form, consultant_id: form.consultant_id || null, override, reason });
      toast.success("Visit updated");
      onUpdated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.conflicts) {
        const reason2 = window.prompt("Hard conflict. Enter override reason to proceed, or cancel:");
        if (reason2) return save(true, reason2);
      } else toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Modal open onOpenChange={onClose} title={visit.job_title || visit.service_name} testId="visit-detail-modal"
      description={`${visit.visit_ref} · ${visit.client_name} · ${visit.site_name}`}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="Status"><StatusBadge status={visit.status} /></Info>
        <Info label="Priority"><PriorityBadge priority={visit.priority} /></Info>
        <Info label="Service">{visit.service_name || "—"}</Info>
        <Info label="Business unit">{visit.business_unit_name || "—"}</Info>
        {"charge" in visit && <Info label="Charge">{fmtMoney(visit.charge)}</Info>}
        {"consultant_cost" in visit && <Info label="Consultant cost">{fmtMoney(visit.consultant_cost)}</Info>}
      </div>

      {conflicts.length > 0 && (
        <div className="space-y-1.5">
          {conflicts.map((c, i) => (
            <div key={i} className={`text-xs rounded px-2 py-1.5 flex items-center gap-1.5 ${c.severity === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
              <AlertTriangle size={12} /> {c.message}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={openReport} data-testid="report-pdf-btn"
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"><FileText size={15} /> Report PDF</button>
        {canComplete && !["Completed", "Closed", "Invoiced", "Cancelled", "Ready for Invoicing"].includes(visit.status) && (
          <button onClick={() => onComplete(visit.id)} data-testid="open-complete-btn"
            className="px-3 py-2 text-sm rounded-lg bg-seq-green text-white hover:bg-seq-greenhover">Complete job</button>
        )}
        {canComms && (
          <button onClick={sendConfirmation} data-testid="send-confirmation-btn"
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Send confirmation email</button>
        )}
      </div>

      {(canSchedule || canReassign) && (
        <div className="border-t border-slate-100 pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Date</label>
              <input type="date" data-testid="visit-date-input" value={form.scheduled_date || ""} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Start time</label>
              <input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Consultant</label>
              <select data-testid="visit-consultant-select" value={form.consultant_id} onChange={(e) => setForm({ ...form, consultant_id: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm">
                <option value="">Unassigned</option>
                {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Status</label>
              <select data-testid="visit-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm">
                {["Draft", "Proposed", "Awaiting Client Confirmation", "Confirmed", "Published", "Scheduled", "In Progress", "Completed", "Report Pending", "Ready for Invoicing", "Cancelled", "Postponed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Close</button>
            <button data-testid="visit-save-btn" disabled={saving} onClick={() => save()} className="px-4 py-2 text-sm bg-seq-green hover:bg-seq-greenhover text-white rounded-lg flex items-center gap-2">
              {saving && <Loader2 size={15} className="animate-spin" />} Save changes
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const Info = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-slate-700 mt-0.5">{children}</div></div>
);
